import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Feather } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const token = params.get("token") || "";
  const { setUser } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    if (!token) { toast.error("Missing reset token"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset", { token, password });
      localStorage.setItem("humanly_token", data.token);
      setUser(data.user);
      toast.success("Password updated. You're signed in.");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <span className="w-8 h-8 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] flex items-center justify-center"><Feather size={16} /></span>
          <span className="font-display text-xl">Humanly</span>
        </Link>
        <div className="surface p-8">
          <h1 className="font-display text-3xl mb-2">Choose a new password</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">At least 6 characters.</p>
          {!token && (
            <div className="rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm p-3 mb-4">
              Missing reset token. Use the link from your reset email.
            </div>
          )}
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label-eyebrow block mb-2">New password</label>
              <input
                data-testid="reset-password"
                type="password" required minLength={6}
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white border border-[hsl(var(--border))] rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/30 focus:border-[hsl(var(--primary))]"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Confirm password</label>
              <input
                data-testid="reset-confirm"
                type="password" required minLength={6}
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className="w-full bg-white border border-[hsl(var(--border))] rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/30 focus:border-[hsl(var(--primary))]"
              />
            </div>
            <button
              data-testid="reset-submit"
              type="submit" disabled={loading || !token}
              className="btn-primary w-full disabled:opacity-60"
            >
              {loading ? "Saving…" : "Set new password"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
